import { Col, Collapse, CollapseProps, Row, Typography } from 'antd';
import { openingBalanceApi } from 'Api/openingBalance';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toastText } from 'utils/utils';
import styles from './index.module.scss';

const OpeningBalance = () => {
	const [searchParams] = useSearchParams();

	const [openingBalance, setOpeningBalance] = useState<any>({
		balanceAsOfDate: '',
		grossPayYTD: null,
		taxPaidYTD: null,
		napsaYTD: null,
		nhimaYTD: null,
		gratuityPay: null,
		amount: null,
		totalInstallments: null,
		remainingInstallments: null,
		leaveEncashAmount: null,
		leaveDays: null,
		addToNextPayroll: false,
	});

	const fetchAllOpeningBalance = async () => {
		const employeeId = searchParams.get('employeeId');

		if (!employeeId) {
			toastText('Employee ID is missing', 'error');
			return;
		}

		try {
			const response =
				await openingBalanceApi.fetchOpeningBalanceByEmployeeId(
					employeeId
				);
			const data = response?.data?.data;

			setOpeningBalance({
				balanceAsOfDate: data.balanceAsOfDate,
				grossPayYTD: data.grossPayYTD,
				taxPaidYTD: data.taxPaidYTD,
				napsaYTD: data.napsaYTD,
				nhimaYTD: data.nhimaYTD,
				gratuityPay: data.gratuityPay,
				amount: data.loanDetails.amount,
				totalInstallments: data.loanDetails.totalInstallments,
				remainingInstallments: data.loanDetails.remainingInstallments,
				leaveEncashAmount: data.leaveEncashAmount,
				leaveDays: data.leaveDays,
				addToNextPayroll: data.addToNextPayroll ?? false,
			});
		} catch (err) {
			toastText('Failed to load opening balance details', 'error');
		}
	};

	useEffect(() => {
		fetchAllOpeningBalance();
	}, []);

	const { Text } = Typography;

	const items: CollapseProps['items'] = [
		{
			key: '1',
			label: (
				<div
					style={{ fontSize: '1.8rem', fontWeight: 500 }}
					className="color-purple"
				>
					Balance as of Date
				</div>
			),
			children: (
				<Row className={styles['form-container-card']} gutter={50}>
					<Col span={12} className={styles['col']}>
						<Text strong>Balance as of Date : </Text>
						<Text>
							{openingBalance.balanceAsOfDate
								? dayjs(openingBalance.balanceAsOfDate).format(
										'DD/MM/YYYY'
								  )
								: '—'}
						</Text>
						<Text type="secondary" className={styles['smallText']}>
							The date as of which these opening balances are
							calculated.
						</Text>
					</Col>
					<Col span={12} className={styles['col']}>
						<Text strong>Add to Next Payroll : </Text>
						<Text>
							{openingBalance.addToNextPayroll
								? 'Yes'
								: openingBalance.addToNextPayroll === false
								? 'No'
								: '-'}
						</Text>
						<Text type="secondary" className={styles.smallText}>
							Indicates whether the leave and gratuity pay should
							be added to the next payroll cycle.
						</Text>
					</Col>
				</Row>
			),
		},
		{
			key: '2',
			label: (
				<div
					style={{ fontSize: '1.8rem', fontWeight: 500 }}
					className="color-purple"
				>
					Year-to-Date Figures
				</div>
			),
			children: (
				<Row className={styles['form-container-card']} gutter={50}>
					<Col span={24}>
						<Text type="secondary" className={styles.helpText}>
							Enter the cumulative YTD amounts from previous
							employment (All amounts in ZMW):
						</Text>
					</Col>

					<Col span={12} className={styles['col']}>
						<Text strong>Gross Pay YTD (ZMW) : </Text>
						<Text>{openingBalance.grossPayYTD || '0'}</Text>
					</Col>

					<Col span={12} className={styles['col']}>
						<Text strong>PAYE YTD (ZMW) : </Text>
						<Text>{openingBalance.taxPaidYTD || '0'}</Text>
					</Col>

					<Col span={12} className={styles['col']}>
						<Text strong>NAPSA YTD (ZMW) : </Text>
						<Text>{openingBalance.napsaYTD || '0'}</Text>
					</Col>

					<Col span={12} className={styles['col']}>
						<Text strong>NHIMA YTD (ZMW) : </Text>
						<Text>{openingBalance.nhimaYTD || '0'}</Text>
					</Col>

					<Col span={12} className={styles['col']}>
						<Text strong>Gratuity Pay (ZMW) : </Text>
						<Text>{openingBalance.gratuityPay || '0'}</Text>
						<Text type="secondary" className={styles.smallText}>
							Total gratuity amount accrued from previous
							employment.
						</Text>
					</Col>
				</Row>
			),
		},
		{
			key: '3',
			label: (
				<div
					style={{ fontSize: '1.8rem', fontWeight: 500 }}
					className="color-purple"
				>
					Loan Balances
				</div>
			),
			children: (
				<Row className={styles['form-container-card']} gutter={50}>
					<Col span={24}>
						<Text type="secondary" className={styles.helpText}>
							Loan details to calculate monthly deduction amounts
						</Text>
					</Col>

					<Col span={12} className={styles['col']}>
						<Text strong>Total Loan Amount : </Text>
						<Text>{openingBalance.amount || '0'}</Text>
						<Text type="secondary" className={styles.smallText}>
							Total amount of loan taken
						</Text>
					</Col>

					<Col span={12} className={styles['col']}>
						<Text strong>Remaining Installments : </Text>
						<Text>
							{openingBalance.remainingInstallments || '0'}
						</Text>
						<Text type="secondary" className={styles.smallText}>
							Number of remaining installments to be paid
						</Text>
					</Col>
				</Row>
			),
		},
		{
			key: '4',
			label: (
				<div
					style={{ fontSize: '1.8rem', fontWeight: 500 }}
					className="color-purple"
				>
					Leave Encashment Balances
				</div>
			),
			children: (
				<Row className={styles['form-container-card']} gutter={50}>
					<Col span={24}>
						<Text type="secondary" className={styles.helpText}>
							Carried forward leave days eligible for year-end
							encashment
						</Text>
					</Col>

					<Col span={12} className={styles['col']}>
						<Text strong>Leave Encashment Amount : </Text>
						<Text>{openingBalance.leaveEncashAmount || '0'}</Text>
						<Text type="secondary" className={styles.smallText}>
							Total encashed amount from previous employment
						</Text>
					</Col>
					<Col span={12} className={styles['col']}>
						<Text strong>Number of Leave Days : </Text>
						<Text>{openingBalance.leaveDays || '0'}</Text>
						<Text type="secondary" className={styles.smallText}>
							Total encashed Leave Days from previous employment
						</Text>
					</Col>

					<Col span={24}>
						<div className={styles.noteBox}>
							<Text className={styles.noteText}>
								<strong>Note:</strong> Leave encashment
								calculations will be performed at year-end based
								on these opening balances plus any additional
								leave accrued during the current employment
								period.
							</Text>
						</div>
					</Col>
				</Row>
			),
		},
	];

	return (
		<div className={styles.container}>
			<div className={styles.header}>
				<Text type="secondary" className={styles.subtitle}>
					Record previous employment YTD figures and balances for
					seamless payroll transition
				</Text>
			</div>

			<div className={styles.scrollableContent}>
				<div className={styles['employment']}>
					<Collapse
						items={items}
						defaultActiveKey={['1', '2', '3', '4']}
						expandIconPosition="end"
						className={styles['employment--employeeDetails']}
						expandIcon={({ isActive }) => (
							<div
								style={{
									transform: `rotate(${
										isActive ? -90 : 0
									}deg)`,
									transition: '0.3s',
								}}
							>
								<svg
									xmlns="http://www.w3.org/2000/svg"
									width="16"
									height="16"
									fill="currentColor"
									className="bi bi-chevron-right"
									viewBox="0 0 16 16"
								>
									<path
										fillRule="evenodd"
										d="M1.646 4.646a.5.5 0 0 1 0 .708l6 6a.5.5 0 0 1-.708.708l-6-6a.5.5 0 0 1 0-.708l6-6a.5.5 0 0 1 .708.708L2.707 4.5 1.646 5.561a.5.5 0 0 1-.708-.708z"
									/>
								</svg>
							</div>
						)}
					/>
				</div>
			</div>
		</div>
	);
};

export default OpeningBalance;
